import React, { useState } from "react";
import { toast } from "react-toastify";
import { Card, Button, TextArea } from "../../../../shared";
import { useAddLeadNoteMutation } from "../../../../redux/services/leads/leadsApi";

/**
 * Internal note thread for a Lead. Mirrors the spirit of
 * SuperAdmin/Feedback/SidebarContent/Notes but is wired to the new
 * `/admin/leads/:id/notes` endpoint via RTK Query so the activity
 * log is updated automatically server-side.
 */
const NotesThread = ({ leadId, notes }) => {
  const [body, setBody] = useState("");
  const [addNote, { isLoading }] = useAddLeadNoteMutation();

  const sortedNotes = [...(notes || [])].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await addNote({ id: leadId, body: trimmed }).unwrap();
      setBody("");
      toast("Note added", { type: "success" });
    } catch (err) {
      toast(err?.data?.message || "Unable to add note", { type: "error" });
    }
  };

  return (
    <Card title="Internal notes">
      <div className="flex flex-col gap-2">
        <TextArea
          rows={3}
          placeholder="Add a private note for the team…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex justify-end">
          <Button
            type="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!body.trim() || isLoading}
          >
            {isLoading ? "Saving…" : "Add note"}
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {sortedNotes.length === 0 ? (
          <p className="text-sm text-gray-500">No notes yet.</p>
        ) : (
          sortedNotes.map((note) => (
            <div
              key={note._id || `${note.createdAt}-${note.body.slice(0, 12)}`}
              className="border border-gray-200 rounded-md px-3 py-2 bg-gray-50"
            >
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{formatAuthor(note.authorId)}</span>
                <span>{formatDate(note.createdAt)}</span>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {note.body}
              </p>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

const formatAuthor = (author) => {
  if (!author) return "Team member";
  if (typeof author === "string") return "Team member";
  const name = `${author.firstName || ""} ${author.lastName || ""}`.trim();
  return name || author.email || "Team member";
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
};

export default NotesThread;
